export const HEALTH_CHECK_KEYS = [
  "unchained",
  "negative_balance",
  "triple_gate",
  "over_allocation",
] as const;

export const HEALTH_UNCHECKED = "unchecked";
export const HEALTH_CLEAN = "clean";
export const HEALTH_ISSUES = "issues";
export const HEALTH_UNAVAILABLE = "unavailable";

export type HealthState =
  | typeof HEALTH_UNCHECKED
  | typeof HEALTH_CLEAN
  | typeof HEALTH_ISSUES
  | typeof HEALTH_UNAVAILABLE;

export type StockHealthSummary = {
  state: HealthState;
  failing: string[];
  xrayAt: string | null;
};

export const HEALTH_LABELS: Record<HealthState, string> = {
  [HEALTH_UNCHECKED]: "Not yet checked",
  [HEALTH_CLEAN]: "Healthy",
  [HEALTH_ISSUES]: "Issues found",
  [HEALTH_UNAVAILABLE]: "Health unavailable",
};

type StockHealth = Record<string, unknown> | null | undefined;

// the inspector serves each check nested ({triple_gate: {pass}}), the store-doctor listing
// flattens it ({triple_gate_pass}). both payloads reach this file, so read either.
function passFlag(health: Record<string, unknown>, key: string): unknown {
  const nested = health[key];
  if (nested && typeof nested === "object") {
    return (nested as { pass?: unknown }).pass;
  }
  return health[`${key}_pass`];
}

function failed(pass: unknown): boolean {
  if (pass === null || pass === undefined) return false;
  return String(pass) !== "true";
}

/** Reads a stock's health, keeping "never X-rayed" apart from "checked and clean". */
export function summariseStockHealth(
  health: StockHealth,
  unavailable = false
): StockHealthSummary {
  if (unavailable) {
    return { state: HEALTH_UNAVAILABLE, failing: [], xrayAt: null };
  }
  // health stays null until X-ray has actually run, so there is no verdict to report yet
  if (!health) {
    return { state: HEALTH_UNCHECKED, failing: [], xrayAt: null };
  }

  const xrayAt = typeof health.xray_at === "string" ? health.xray_at : null;
  const failing = HEALTH_CHECK_KEYS.filter((key) =>
    failed(passFlag(health, key))
  );

  return {
    state: failing.length ? HEALTH_ISSUES : HEALTH_CLEAN,
    failing: [...failing],
    xrayAt,
  };
}

export function healthCheckLabel(key: string): string {
  return key.replace(/_/g, " ");
}
